import environment from '../config/environment';

// Utility to prevent duplicate API calls with better tracking
let pendingRequests = new Map();
let completedRequests = new Map(); // Changed to Map to store cached results

export const apiCallWithCache = async (key, apiCall) => {
  // If there's already a pending request with this key, return it
  if (pendingRequests.has(key)) {
    console.log(`Returning existing pending request for: ${key}`);
    return pendingRequests.get(key);
  }

  // If this request has already been completed successfully, return cached result
  if (completedRequests.has(key)) {
    console.log(`Request ${key} already completed, returning cached result...`);
    return Promise.resolve(completedRequests.get(key));
  }

  console.log(`Starting new API call for: ${key}`);
  
  // Create new promise for this API call
  const promise = apiCall()
    .then(result => {
      console.log(`API call ${key} completed successfully`);
      completedRequests.set(key, result); // Store the result in cache
      return result;
    })
    .catch(error => {
      console.error(`API call ${key} failed:`, error);
      throw error;
    })
    .finally(() => {
      // Remove from pending requests when done
      pendingRequests.delete(key);
      console.log(`Removed ${key} from pending requests`);
    });

  // Store the promise
  pendingRequests.set(key, promise);
  
  return promise;
};

// Utility to clear pending requests (useful for cleanup)
export const clearPendingRequests = () => {
  console.log('Clearing all pending requests');
  pendingRequests.clear();
  completedRequests.clear();
};

// Utility to clear a specific cache entry
export const clearCacheEntry = (key) => {
  console.log(`Clearing cache entry for: ${key}`);
  pendingRequests.delete(key);
  completedRequests.delete(key);
};

// Utility to check if component is still mounted
export const createMountedRef = () => {
  const ref = { current: true };
  return ref;
}; 

// API utility functions for handling retries and error recovery

/**
 * Retry function for API calls with exponential backoff
 * @param {Function} apiCall - The API function to retry
 * @param {number} maxRetries - Maximum number of retries (default from environment)
 * @param {number} baseDelay - Base delay in milliseconds (default from environment)
 * @returns {Promise} - Promise that resolves with the API response
 */
export const retryApiCall = async (apiCall, maxRetries = environment.API_RETRY_ATTEMPTS || 2, baseDelay = environment.API_RETRY_DELAY || 3000) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await apiCall();
      return result;
    } catch (error) {
      lastError = error;
      
      // Don't retry on certain errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        throw error;
      }
      
      // If this is the last attempt, throw the error
      if (attempt === maxRetries) {
        console.error(`API call failed after ${maxRetries + 1} attempts:`, error);
        throw error;
      }
      
      // Check if it's a chunked encoding error or network error that should be retried
      const shouldRetry = 
        error.message?.includes('ERR_INCOMPLETE_CHUNKED_ENCODING') ||
        error.code === 'ERR_NETWORK' ||
        error.message === 'Network Error' ||
        error.code === 'ECONNABORTED' ||
        !error.response; // No response means network issue
      
      if (!shouldRetry) {
        throw error;
      }
      
      // Calculate delay with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      console.warn(`API call failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms:`, error.message);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

/**
 * Wrapper for API calls that handles common error patterns
 * @param {Function} apiCall - The API function to execute
 * @param {string} apiName - Name of the API for logging
 * @returns {Promise} - Promise that resolves with the API response
 */
export const executeApiCall = async (apiCall, apiName = 'API') => {
  try {
    return await retryApiCall(apiCall);
  } catch (error) {
    console.error(`${apiName} failed:`, error);
    
    // Show user-friendly error message
    if (error.message && error.message.includes('ERR_INCOMPLETE_CHUNKED_ENCODING')) {
      console.error(`${apiName}: Network connection issue detected. Please check your internet connection.`);
    } else if (error.code === 'ECONNABORTED') {
      console.error(`${apiName}: Request timed out. Please try again.`);
    } else if (error.response?.status >= 500) {
      console.error(`${apiName}: Server error. Please try again later.`);
    } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      console.error(`${apiName}: Network error. Please check your connection.`);
    }
    
    throw error;
  }
}; 